import * as Tone from 'tone';
import { Note } from '../types/note';
import { MidiNote } from '../types/midi';
import { Chord } from '../types/chord';
import { getHint } from '../utils/chordUtils';
import logger from './logger';

let initialized = false;
let synth: Tone.PolySynth | null = null;

// Map MIDI note number to frequency
const getNoteFrequency = (noteNumber: number): number => {
  return 440 * Math.pow(2, (noteNumber - 69) / 12);
};

export const ensureInitialized = async () => {
  if (!initialized) {
    await Tone.start();

    synth = new Tone.PolySynth(Tone.Synth).toDestination();
    synth.set({
      oscillator: {
        type: 'triangle8'
      },
      envelope: {
        attack: 0.001,
        decay: 0.15,
        sustain: 0.2,
        release: 0.1
      }
    });

    logger.debug('Audio context started');
    initialized = true;
  }
  if (synth) {
    synth.context.resume();
  }
}

// Play a single note
export const playNote = async (note: MidiNote | Note, duration = 1.0) => {
  try {
    await ensureInitialized();
    const noteNumber = 'number' in note ? note.number : note.value;
    const velocity = 'number' in note ? note.velocity / 127 : Math.random() * 0.4 + 0.3;
    const freq = getNoteFrequency(noteNumber);
    if (synth) {
      synth.triggerAttackRelease(freq, duration, undefined, velocity);
    }
  } catch (error) {
    logger.error({ err: error }, 'Error playing note');
  }
};

export const stopNote = async (note: number) => {
  try {
    await ensureInitialized();
    if (synth) {
      synth.triggerRelease(getNoteFrequency(note));
    }
  } catch (error) {
    logger.error({ err: error }, 'Error stopping note');
  }
}

// Play a chord
export const playChord = async (chord: Chord, duration = 1.0) => {
  try {
    await ensureInitialized();
    if (synth) {
      const notes: Note[] = getHint(chord, { 'start': 48, 'end': 72 });
      const frequencies = notes.map(note => getNoteFrequency(note.value));
      synth.triggerAttackRelease(frequencies, duration, undefined, 0.5);
    }
  } catch (error) {
    logger.error({ err: error }, 'Error playing chord');
  }
};

